import Color from 'color';
import type { HexColor } from '@getaccept/fried-tofu';
import type { FontSettings } from '../../fonts/types/font';
import type { SigningTheme } from '../types/theme';
import {
  colorLightnessThreshold,
  colorHoverPercentage,
  colorDisabledLightness,
  colorDisabledSaturation,
  colorBoostSecondaryLightNormal,
  colorBoostSecondaryLightHover,
  colorBoostSecondaryLightDisabled,
  colorFadePrimaryLightDisabled,
} from '../constants/branding';

const gray = '#3B3B3B';
const lightGray = '#969696';

export class BrandingHelper {
  static getTextColor(accentColor: string, disabled: boolean, secondary: boolean) {
    const light: boolean = this.colorLight(accentColor);
    if (light) {
      if (!disabled && !secondary) {
        return gray;
      } else if (disabled && !secondary) {
        return lightGray;
      } else if (!disabled && secondary) {
        return new Color(accentColor).darken(colorBoostSecondaryLightNormal).hex();
      } else {
        return new Color(accentColor).darken(colorBoostSecondaryLightDisabled).hex();
      }
    }

    if (!secondary) {
      return '#ffffff';
    } else if (!disabled) {
      return accentColor;
    } else {
      return this.getDisabledColor(accentColor);
    }
  }
  static getBackgroundColor(accentColor: string, disabled: boolean, secondary: boolean) {
    const light: boolean = this.colorLight(accentColor);
    if (light && disabled && !secondary) {
      return new Color(accentColor).fade(colorFadePrimaryLightDisabled);
    }

    if (!disabled && !secondary) {
      return accentColor;
    } else if (disabled && !secondary) {
      return this.getDisabledColor(accentColor);
    } else {
      return '#ffffff';
    }
  }
  static getBackgroundColorHover(accentColor: string, disabled: boolean, secondary: boolean) {
    const light: boolean = this.colorLight(accentColor);
    if (light) {
      if (!disabled && !secondary) {
        return new Color(accentColor).darken(colorHoverPercentage).hex();
      } else if (!disabled && secondary) {
        return new Color(accentColor).fade(0.5);
      }
    }

    if (!disabled && !secondary) {
      return new Color(accentColor).lighten(colorHoverPercentage).hex();
    } else if (!disabled && secondary) {
      return new Color(accentColor).fade(0.9);
    } else {
      return this.getBackgroundColor(accentColor, disabled, secondary);
    }
  }
  static getBorderColor(accentColor: string, disabled: boolean, secondary: boolean) {
    const light: boolean = this.colorLight(accentColor);
    if (light) {
      if (!disabled && secondary) {
        return new Color(accentColor).darken(colorBoostSecondaryLightNormal).hex();
      } else if (disabled && secondary) {
        return new Color(accentColor).darken(colorBoostSecondaryLightDisabled).hex();
      }
    }

    if (!secondary) {
      return 'none';
    } else if (!disabled) {
      return accentColor;
    } else {
      return this.getDisabledColor(accentColor);
    }
  }

  static getBorderColorHover(accentColor: string, disabled: boolean, secondary: boolean) {
    const light: boolean = this.colorLight(accentColor);
    if (light && secondary && !disabled) {
      return new Color(accentColor).darken(colorBoostSecondaryLightHover).hex();
    }
    return this.getBorderColor(accentColor, disabled, secondary);
  }

  static colorLight = (color: string): boolean =>
    new Color(color).lightness() > colorLightnessThreshold;

  static getDisabledColor = (accentColor: string): string => {
    const color: Color = new Color(accentColor).hsl();
    color.color[1] = colorDisabledSaturation;
    color.color[2] = colorDisabledLightness;
    return color.hex();
  };

  static getBackground(theme: SigningTheme, isIE: boolean): string {
    if (!theme) {
      return '';
    }
    if (theme.backgroundImage) {
      return `background-image: url(${theme.backgroundImage});`;
    }
    let path = '/assets/img/background/signing-page-bg.svg';
    if (isIE) {
      path = '/assets/img/background/signing-page-bg.png';
    }

    return `background-image: url(${path})`;
  }

  static getAccentColor(theme: SigningTheme & { accentColor: HexColor }): string {
    if (!theme || !theme.accentColor) {
      return '';
    }
    return `background-color: ${theme.accentColor}; border-color: ${theme.accentColor};`;
  }

  static getLogoUrl(theme: SigningTheme): string {
    const gaWhiteLogo = 'https://media.getaccept.com/logo/x7gpxjpr.png?1528788212';

    if (!theme || theme.logoUrl === gaWhiteLogo) {
      return '';
    }
    return theme.logoUrl || '';
  }

  private static loadImage = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(image.src);
        resolve(image);
      };
      image.onerror = reject;
      image.src = URL.createObjectURL(file);
    });

  private static async resizeImage(
    imageFile: File,
    maxWidth: number,
    maxHeight: number,
    type: 'image/png' | 'image/jpeg' = 'image/jpeg'
  ): Promise<Blob> {
    const image = await this.loadImage(imageFile);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const aspectRatio = image.width / image.height;
    let newWidth, newHeight;

    if (image.width > image.height) {
      newWidth = maxWidth;
      newHeight = Math.round(maxWidth / aspectRatio);
    } else {
      newHeight = maxHeight;
      newWidth = Math.round(maxHeight * aspectRatio);
    }

    canvas.width = newWidth;
    canvas.height = newHeight;

    ctx?.drawImage(image, 0, 0, newWidth, newHeight);

    if (type === 'image/jpeg') {
      return new Promise<Blob>(resolve => canvas.toBlob(resolve, type, 0.85));
    }

    return new Promise<Blob>(resolve => canvas.toBlob(resolve, type));
  }

  static async processEntityLogoImageFile(file: File, entityId: string): Promise<File> {
    if (!file || file.type !== 'image/png') {
      return;
    }

    const image = await this.loadImage(file);

    if (image.width >= 500 || image.height >= 500) {
      const resizedBlob = await this.resizeImage(file, 500, 200, 'image/png');

      return new File([resizedBlob!], `${entityId}.png`, { type: 'image/png' });
    }

    return new File([file], `${entityId}.png`, { type: 'image/png' });
  }

  static async readURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => resolve(e.target.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  static async processBackgroundImageFile(file: File, themeId: string): Promise<File> {
    if (!file || !['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      return;
    }

    const image = await this.loadImage(file);

    if (image.width >= 1500 || image.height >= 1500) {
      const resizedBlob = await this.resizeImage(file, 1500, 1500, 'image/jpeg');

      return new File([resizedBlob!], `${themeId}.jpg`, { type: 'image/jpeg' });
    }

    return new File([file], `${themeId}.jpg`, { type: 'image/jpeg' });
  }

  static removeTypenameFromFontSettings = (fontSettings: FontSettings): FontSettings => {
    const { __typename, ...rest } = fontSettings;
    const { __typename: defaultTypeName, ...defaultFont } = rest.defaultFont;
    const { __typename: headingsTypeName, ...headingsFont } = rest.headingsFont;

    return {
      ...rest,
      defaultFont: {
        ...defaultFont,
        sources: defaultFont.sources.map(({ __typename: sourceTypeName, ...source }) => source),
      },
      headingsFont: {
        ...headingsFont,
        sources: headingsFont.sources.map(({ __typename: sourceTypeName, ...source }) => source),
      },
    };
  };
}
